// ChartWidget.js
// One `kind: 'chart'` manifest entry rendered as a chart card.
//
// A chart's display settings (grid, legend, bar width…) are stored under
// `chart_settings[source_block_id][entry_key]` on the function head, keyed by
// the same binding identity a widget uses. The card (WidgetCard) hands this
// widget its entry's settings from the View's live head watch, so a save shows
// here — and on every other copy of the chart — the moment it lands.

import React, { useMemo } from 'react';
import { Alert, Box } from '@mui/material';
import BarChartComponent from '../../charts/BarChart';
import LineChartComponent from '../../charts/LineChart';
import ScatterChartComponent from '../../charts/ScatterChart';
import PieChartComponent from '../../charts/PieChart';
import BoxAndWhiskerChartComponent from '../../charts/BoxAndWhiskerChart';
import SuperChartComponent from '../../charts/SuperChart';
import ChartSettingsButton from '../../charts/ChartSettingsButton';
import { applyChartSettings } from '../../common/CompositeOutputTabs';

// These two maps mirror the ones inside CompositeOutputTabs, which does not
// export them. They must stay in step with it — a chart type added there is
// unsupported here until it is added below as well.
const CHART_COMPONENTS = {
  bar: BarChartComponent,
  line: LineChartComponent,
  scatter: ScatterChartComponent,
  pie: PieChartComponent,
  box_and_whisker: BoxAndWhiskerChartComponent,
  superchart: SuperChartComponent,
};

const CHART_SETTINGS_TYPES = {
  bar: 'bar_chart',
  line: 'line_chart',
  scatter: 'scatter_chart',
  pie: 'pie_chart',
  box_and_whisker: 'box_and_whisker_chart',
  superchart: 'superchart',
};

function ChartWidget({
  entry,
  functionId = null,
  companyName = null,
  savedSettings = null,
}) {
  const chart = entry?.chart || null;
  const sourceBlockId = entry?.source_block_id || null;
  const entryKey = entry?.key || null;

  const renderedChart = useMemo(
    () => applyChartSettings(chart, savedSettings),
    [chart, savedSettings]
  );
  // The frame already prints this chart's title in the card's title row, so the
  // renderer is told not to print it again above the plot — the same words
  // twice, one under the other. The title stays on the chart itself: the
  // export still carries it, and it is what the settings dialog edits.
  const ChartComponent = CHART_COMPONENTS[renderedChart?.type];

  if (!ChartComponent) {
    return <Alert severity="warning">Unsupported chart type: {renderedChart?.type || 'unknown'}</Alert>;
  }

  // The chart fills the widget body: the card, not the chart, owns the height.
  //
  // The chart settings control sits at the BOTTOM right rather than the top,
  // because the widget frame's own ⚙ (card display) occupies the top-right
  // corner on hover and two gears in one corner is unreadable. CompositeOutputTabs
  // keeps its control at the top: there is no widget frame there to collide with.
  return (
    <Box sx={{ position: 'relative', height: '100%', minHeight: 180 }}>
      {companyName && functionId && sourceBlockId && entryKey && (
        <Box sx={{ position: 'absolute', bottom: 0, right: 0, zIndex: 2 }}>
          <ChartSettingsButton
            functionId={functionId}
            sourceBlockId={sourceBlockId}
            entryKey={entryKey}
            chartSpec={chart}
            chartType={CHART_SETTINGS_TYPES[renderedChart.type]}
            companyName={companyName}
            sx={{ padding: '4px', borderRadius: '6px' }}
          />
        </Box>
      )}
      <ChartComponent chartData={renderedChart} showTitle={false} />
    </Box>
  );
}

export default ChartWidget;
